/**
 * Column type registry entry point. Adding a column type = adding one folder here plus one
 * line in `registerBuiltInColumnTypes()`. Nothing else in the codebase changes (CLAUDE.md #6).
 *
 * The twelve tier-1 types of `specs/M2-tier1-column-types.md` plus the deprecated `person`
 * column (aliased to monday's second enum name `item_assignees`), and the twenty tier-2/3
 * types of `specs/M2-tier2-column-types.md`.
 *
 * Not registered, deliberately: `week`, `vote`, `color_picker`, `team`, `doc`, `direct_doc`,
 * `integration`, `group`. None has a single column on an active board — `week` and `vote`
 * exist only on archived ones `[cvs§1]` — so they fall through to `unsupported`, which
 * round-trips their value untouched (M1-B5 R1) instead of shipping a guess.
 */
import { columnTypeRegistry, registerColumnType, registerColumnTypeAlias, setUnsupportedColumnType, } from './registry.js';
import { autoNumberColumn } from './auto-number/index.js';
import { boardRelationColumn } from './board-relation/index.js';
import { buttonColumn } from './button/index.js';
import { checkboxColumn } from './checkbox/index.js';
import { countryColumn } from './country/index.js';
import { creationLogColumn } from './creation-log/index.js';
import { dateColumn } from './date/index.js';
import { dependencyColumn } from './dependency/index.js';
import { dropdownColumn } from './dropdown/index.js';
import { emailColumn } from './email/index.js';
import { fileColumn } from './file/index.js';
import { formulaColumn } from './formula/index.js';
import { hourColumn } from './hour/index.js';
import { itemIdColumn } from './item-id/index.js';
import { lastUpdatedColumn } from './last-updated/index.js';
import { linkColumn } from './link/index.js';
import { locationColumn } from './location/index.js';
import { longTextColumn } from './long-text/index.js';
import { mirrorColumn } from './mirror/index.js';
import { nameColumn } from './name/index.js';
import { numbersColumn } from './numbers/index.js';
import { peopleColumn } from './people/index.js';
import { phoneColumn } from './phone/index.js';
import { progressColumn } from './progress/index.js';
import { ratingColumn } from './rating/index.js';
import { PERSON_ALIAS_TYPE, personColumn } from './person/index.js';
import { statusColumn } from './status/index.js';
import { subtasksColumn } from './subtasks/index.js';
import { tagsColumn } from './tags/index.js';
import { textColumn } from './text/index.js';
import { timeTrackingColumn } from './time-tracking/index.js';
import { timelineColumn } from './timeline/index.js';
import { unsupportedColumn } from './unsupported/index.js';
import { worldClockColumn } from './world-clock/index.js';
export * from './types.js';
export * from './sql.js';
export * from './filter-conditions.js';
export * from './helpers.js';
export * from './summary-format.js';
export * from './registry.js';
export * from './legacy.js';
export * from './column-ids.js';
export * from './name/index.js';
export * from './status/index.js';
export * from './text/index.js';
export * from './long-text/index.js';
export * from './numbers/index.js';
export * from './date/index.js';
export * from './people/index.js';
export * from './person/index.js';
export * from './dropdown/index.js';
export * from './subtasks/index.js';
export * from './last-updated/index.js';
export * from './creation-log/index.js';
export * from './checkbox/index.js';
export * from './link/index.js';
export * from './tags/index.js';
export * from './timeline/index.js';
export * from './file/index.js';
export * from './location/index.js';
export * from './world-clock/index.js';
export * from './time-tracking/index.js';
export * from './phone/index.js';
export * from './email/index.js';
export * from './rating/index.js';
export * from './hour/index.js';
export * from './country/index.js';
export * from './item-id/index.js';
export * from './auto-number/index.js';
export * from './progress/index.js';
export * from './button/index.js';
export * from './formula/index.js';
export * from './board-relation/index.js';
export * from './mirror/index.js';
export * from './dependency/index.js';
export * from './unsupported/index.js';
let registered = false;
/** Idempotent: importing this module from several entry points must not double-register. */
export function registerBuiltInColumnTypes() {
    if (registered)
        return;
    registered = true;
    registerColumnType(nameColumn);
    registerColumnType(statusColumn);
    registerColumnType(textColumn);
    registerColumnType(longTextColumn);
    registerColumnType(numbersColumn);
    registerColumnType(dateColumn);
    registerColumnType(peopleColumn);
    registerColumnType(personColumn);
    registerColumnType(dropdownColumn);
    registerColumnType(subtasksColumn);
    registerColumnType(lastUpdatedColumn);
    registerColumnType(creationLogColumn);
    registerColumnType(checkboxColumn);
    /*
     * Tier 2 / 3 (`specs/M2-tier2-column-types.md`), in the account's usage order: `link`
     * (5,602 populated cells) · `tags` (1,793) · `timeline` (311) · `file` (175) · `location`
     * (137) · `world_clock` (126) · `time_tracking` (122) · `phone` (114) · `email` (44) ·
     * `rating` (43) · `hour` (42) · `country` (28) · `item_id` (2,063, derived) ·
     * `auto_number` · `progress` · `button` (566) · then the computed/relation four.
     */
    registerColumnType(linkColumn);
    registerColumnType(tagsColumn);
    registerColumnType(timelineColumn);
    registerColumnType(fileColumn);
    registerColumnType(locationColumn);
    registerColumnType(worldClockColumn);
    registerColumnType(timeTrackingColumn);
    registerColumnType(phoneColumn);
    registerColumnType(emailColumn);
    registerColumnType(ratingColumn);
    registerColumnType(hourColumn);
    registerColumnType(countryColumn);
    registerColumnType(itemIdColumn);
    registerColumnType(autoNumberColumn);
    registerColumnType(progressColumn);
    registerColumnType(buttonColumn);
    registerColumnType(formulaColumn);
    registerColumnType(boardRelationColumn);
    registerColumnType(mirrorColumn);
    registerColumnType(dependencyColumn);
    // The lossless fall-back for the types tier 2/3 has not modelled yet (M1-B5 R1).
    registerColumnType(unsupportedColumn);
    setUnsupportedColumnType(unsupportedColumn);
    // monday's enum spells the deprecated person column both ways `[09§A.7 deprecated]`.
    registerColumnTypeAlias(PERSON_ALIAS_TYPE, personColumn.type);
}
registerBuiltInColumnTypes();
export { columnTypeRegistry };
//# sourceMappingURL=index.js.map